import { Head, Link, router } from '@inertiajs/react';
import { useState } from 'react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import Table, { Th, Td, Tr } from '@/Components/Table';
import PageHeader from '@/Components/PageHeader';
import EmptyState from '@/Components/EmptyState';
import FormInput from '@/Components/FormInput';
import FormSelect from '@/Components/FormSelect';
import Alert from '@/Components/Alert';
import Modal, { ModalBody, ModalFooter } from '@/Components/Modal';
import { PlusIcon, EyeIcon, PencilSquareIcon, TrashIcon, EnvelopeIcon, UsersIcon, MagnifyingGlassIcon, FunnelIcon, XMarkIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';

export default function Index({ visitors, exhibitions: initialExhibitions, countries, flash }) {
    const [search, setSearch] = useState('');
    const [type, setType] = useState('');
    const [exhibition, setExhibition] = useState('');
    const [country, setCountry] = useState('');
    const [emailStatus, setEmailStatus] = useState('');
    const [registeredBy, setRegisteredBy] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Exhibition management
    const [exhibitions, setExhibitions] = useState(initialExhibitions || []);
    const [showExhibitionModal, setShowExhibitionModal] = useState(false);
    const [newExhibitionName, setNewExhibitionName] = useState('');
    const [exProcessing, setExProcessing] = useState(false);
    const [exError, setExError] = useState('');
    const [exSuccess, setExSuccess] = useState('');

    const createExhibition = async () => {
        if (!newExhibitionName.trim()) return;
        setExProcessing(true);
        setExError('');
        setExSuccess('');
        try {
            const res = await fetch('/exhibitions', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ name: newExhibitionName.trim() }),
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setExhibitions(prev => [...prev, data.exhibition]);
                setNewExhibitionName('');
                setExSuccess(data.message || 'Exhibition created!');
                setTimeout(() => setExSuccess(''), 3000);
            } else {
                setExError(data.message || 'Failed to create exhibition.');
            }
        } catch {
            setExError('Network error.');
        }
        setExProcessing(false);
    };

    const deleteExhibition = async (id) => {
        try {
            const res = await fetch(`/exhibitions/${id}`, {
                method: 'DELETE',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
                    'Accept': 'application/json',
                },
            });
            const data = await res.json();
            if (res.ok && data.success) {
                setExhibitions(prev => prev.filter(e => e.id !== id));
            } else {
                setExError(data.message || 'Failed to delete.');
            }
        } catch {
            setExError('Network error.');
        }
    };

    const applyFilters = (e) => {
        e?.preventDefault();
        const params = {};
        if (search) params.search = search;
        if (type) params.type = type;
        if (exhibition) params.exhibition = exhibition;
        if (country) params.country = country;
        if (emailStatus) params.email_status = emailStatus;
        if (registeredBy) params.registered_by = registeredBy;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        router.get('/visitors', params, { preserveState: true });
    };

    const clearFilters = () => {
        setSearch('');
        setType('');
        setExhibition('');
        setCountry('');
        setEmailStatus('');
        setRegisteredBy('');
        setDateFrom('');
        setDateTo('');
        router.get('/visitors');
    };

    const hasFilters = search || type || exhibition || country || emailStatus || registeredBy || dateFrom || dateTo;

    return (
        <AppLayout>
            <Head title="Visitors" />
            <PageHeader
                title="Visitors"
                subtitle={`Manage exhibition visitors${visitors ? ` (${visitors.length} total)` : ''}`}
                actions={
                    <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowExhibitionModal(true)}>
                            <BuildingStorefrontIcon className="w-4 h-4" /> Add Exhibition
                        </Button>
                        <Button href="/visitors/create" size="sm">
                            <PlusIcon className="w-4 h-4" /> Add Visitor
                        </Button>
                    </div>
                }
            />

            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}
            {flash?.error && <Alert type="danger" className="mb-4">{flash.error}</Alert>}

            {/* Filters */}
            <Card className="mb-6">
                <CardBody>
                    <form onSubmit={applyFilters} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <div className="relative sm:col-span-2">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search by name, company, email, phone..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                                />
                            </div>
                            <FormSelect value={type} onChange={e => setType(e.target.value)}>
                                <option value="">All Types</option>
                                <option value="Customer">Customer</option>
                                <option value="Supplier">Supplier</option>
                            </FormSelect>
                            <FormSelect value={exhibition} onChange={e => setExhibition(e.target.value)}>
                                <option value="">All Exhibitions</option>
                                {exhibitions?.map(ex => (
                                    <option key={ex.id} value={ex.id}>{ex.name}</option>
                                ))}
                            </FormSelect>
                            <FormSelect value={country} onChange={e => setCountry(e.target.value)}>
                                <option value="">All Countries</option>
                                {countries?.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </FormSelect>
                            <FormSelect value={emailStatus} onChange={e => setEmailStatus(e.target.value)}>
                                <option value="">Email Status</option>
                                <option value="sent">Email Sent</option>
                                <option value="not_sent">Not Sent</option>
                                <option value="no_email">No Email</option>
                            </FormSelect>
                            <FormInput
                                type="date"
                                value={dateFrom}
                                onChange={e => setDateFrom(e.target.value)}
                                placeholder="From date"
                            />
                            <FormInput
                                type="date"
                                value={dateTo}
                                onChange={e => setDateTo(e.target.value)}
                                placeholder="To date"
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button type="submit" size="sm">
                                <FunnelIcon className="w-4 h-4" /> Apply Filters
                            </Button>
                            {hasFilters && (
                                <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
                                    <XMarkIcon className="w-4 h-4" /> Clear
                                </Button>
                            )}
                        </div>
                    </form>
                </CardBody>
            </Card>

            {/* Table */}
            <Card>
                <Table minWidth="900px">
                    <thead>
                        <tr>
                            <Th>Name</Th>
                            <Th>Company</Th>
                            <Th>Email</Th>
                            <Th>Phone</Th>
                            <Th>Type</Th>
                            <Th>Exhibition</Th>
                            <Th>Email Status</Th>
                            <Th>Date</Th>
                            <Th>Actions</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {visitors?.map(v => (
                            <Tr key={v.id}>
                                <Td className="font-medium text-slate-800 whitespace-nowrap">
                                    {v.personal_contact_name || '—'}
                                </Td>
                                <Td className="text-slate-500 whitespace-nowrap">
                                    {v.company_name || '—'}
                                </Td>
                                <Td className="text-slate-500">
                                    {v.email || '—'}
                                </Td>
                                <Td className="text-slate-500 whitespace-nowrap">
                                    {v.mobile_number
                                        ? `${v.mobile_country_code || ''} ${v.mobile_number}`
                                        : v.telephone
                                            ? `${v.telephone_country_code || ''} ${v.telephone}`
                                            : '—'
                                    }
                                </Td>
                                <Td>
                                    <Badge variant={v.type === 'Supplier' ? 'purple' : v.type === 'Customer' ? 'info' : 'neutral'}>
                                        {v.type || 'N/A'}
                                    </Badge>
                                </Td>
                                <Td className="text-slate-500 whitespace-nowrap">
                                    {v.exhibition?.name || '—'}
                                </Td>
                                <Td>
                                    {v.email_logs_count > 0
                                        ? <Badge variant="success">Sent ({v.email_logs_count})</Badge>
                                        : v.email
                                            ? <Badge variant="warning">Not Sent</Badge>
                                            : <Badge variant="neutral">No Email</Badge>
                                    }
                                </Td>
                                <Td className="text-xs text-slate-500 whitespace-nowrap">
                                    {new Date(v.created_at).toLocaleDateString()}
                                </Td>
                                <Td>
                                    <div className="flex items-center gap-1">
                                        <Link href={`/visitors/${v.id}`} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                                            <EyeIcon className="w-4 h-4 text-slate-500" />
                                        </Link>
                                        <Link href={`/visitors/${v.id}/edit`} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors">
                                            <PencilSquareIcon className="w-4 h-4 text-slate-500" />
                                        </Link>
                                        {v.email && (
                                            <Link href={`/visitors/${v.id}/send-email`} className="p-1.5 hover:bg-emerald-50 rounded-lg transition-colors">
                                                <EnvelopeIcon className="w-4 h-4 text-emerald-700" />
                                            </Link>
                                        )}
                                        <button onClick={() => setDeleteConfirm(v.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition-colors">
                                            <TrashIcon className="w-4 h-4 text-red-400" />
                                        </button>
                                    </div>
                                </Td>
                            </Tr>
                        ))}
                    </tbody>
                </Table>
                {(!visitors || visitors.length === 0) && (
                    <EmptyState
                        icon={UsersIcon}
                        title="No visitors found"
                        description={hasFilters ? 'Try adjusting your filters.' : 'Register your first visitor.'}
                        action={
                            <Button href="/visitors/create" size="sm">
                                <PlusIcon className="w-4 h-4" /> Add Visitor
                            </Button>
                        }
                    />
                )}
            </Card>

            {/* Exhibition Management Modal */}
            <Modal
                show={showExhibitionModal}
                onClose={() => { setShowExhibitionModal(false); setExError(''); setExSuccess(''); }}
                title="Manage Exhibitions"
                iconBg="bg-emerald-100"
                iconColor="text-emerald-600"
                icon={<BuildingStorefrontIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    {exSuccess && <Alert type="success" className="mb-3">{exSuccess}</Alert>}
                    {exError && <Alert type="danger" className="mb-3">{exError}</Alert>}

                    <div className="flex gap-2 mb-4">
                        <input
                            type="text"
                            placeholder="Exhibition name..."
                            value={newExhibitionName}
                            onChange={e => setNewExhibitionName(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); createExhibition(); } }}
                            className="flex-1 rounded-xl border border-slate-200 text-sm px-4 py-2.5 focus:border-emerald-700 focus:ring-2 focus:ring-emerald-700/20"
                        />
                        <Button size="sm" onClick={createExhibition} disabled={exProcessing || !newExhibitionName.trim()}>
                            <PlusIcon className="w-4 h-4" /> Add
                        </Button>
                    </div>

                    {exhibitions.length > 0 ? (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {exhibitions.map(ex => (
                                <div key={ex.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100">
                                    <div>
                                        <span className="text-sm font-medium text-slate-800">{ex.name}</span>
                                        {ex.is_default ? <span className="ml-2 text-[10px] text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded">DEFAULT</span> : null}
                                    </div>
                                    <button
                                        onClick={() => deleteExhibition(ex.id)}
                                        className="p-1 hover:bg-red-50 rounded transition"
                                        title="Delete"
                                    >
                                        <TrashIcon className="w-3.5 h-3.5 text-red-400" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-sm text-slate-400 text-center py-4">No exhibitions yet.</p>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => { setShowExhibitionModal(false); setExError(''); setExSuccess(''); }}>Close</Button>
                </ModalFooter>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                show={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                title="Delete Visitor"
                iconBg="bg-red-100"
                iconColor="text-red-600"
                icon={<TrashIcon className="w-5 h-5" />}
            >
                <ModalBody>
                    <p className="text-sm text-slate-600">
                        Are you sure you want to delete this visitor? This action cannot be undone and will also remove all associated attachments.
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button
                        variant="danger"
                        onClick={() => {
                            router.delete(`/visitors/${deleteConfirm}`, {
                                onSuccess: () => setDeleteConfirm(null),
                            });
                        }}
                    >
                        Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </AppLayout>
    );
}
